import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PulseLoader } from "react-spinners";

import Button from "../../components/Button/Button";
import Checkbox from "../../components/Checkbox/Checkbox";
import Label from "../../components/Label/Label";
import SignatureCanvas from "../../components/SignatureCanvas/SignatureCanvas";
import Header from "../../components/Header/Header";
import { ContractContext } from "../../context/contract";
import NavigationButtons from "../../components/NavigationButtons/NavigationButtons";
import Overlay from "../../components/Overlay/Overlay";
import ResetPermissions from "../../components/ResetPermissions/ResetPermissions";

import { showErrorToast } from "../../utils/toast";
import { dataURItoBlob } from "../../utils/common";

import ReloadIcon from "../../assets/icons/reload.svg";
import variables from "../../assets/styles/colors.scss";

import "./styles.scss";

const CustomerSignature = () => {
  const { formData, handleFormData } = useContext(ContractContext);
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [permissionError, setPermissionError] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const frameCountRef = useRef(0);

  useEffect(() => {
    const getPermissionStatus = async () => {
      return await Promise.all(
        ["camera", "geolocation"].map((name) =>
          navigator.permissions
            .query({ name: name as PermissionName })
            .then((response) => {
              if (response.state === "denied") {
                setPermissionError(true);
              }
            })
        )
      );
    };
    getPermissionStatus();
  });

  useEffect(() => {
    const path = videoRef.current;
    if (navigator.mediaDevices?.getUserMedia && path && !permissionError) {
      setLoading(true);
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          path.srcObject = stream;
          setVideoStream(stream);
        })
        .catch((err) => {
          showErrorToast("Someting went wrong!");
          setLoading(false);
          setPermissionError(true);
        });
    }
  }, [videoRef, permissionError]);

  useEffect(() => () => stopVideoStream(videoStream), [permissionError]);

  const getGeolocation = () => {
    return new Promise((resolve) => {
      let location = null;
      if (
        window.navigator &&
        window.navigator.geolocation &&
        !permissionError
      ) {
        location = window.navigator.geolocation;
      }
      if (location) {
        location.getCurrentPosition((position) => {
          resolve({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
          });
        });
      }
    });
  };

  const stopVideoStream = (videoStream: MediaStream | null) => {
    if (videoStream)
      videoStream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
      });
  };

  const onCheckboxClick = (value: boolean) => {
    handleFormData({
      ...formData,
      customerSignTnC: value,
    });
  };

  const onClearButtonClick = () => {
    formData?.customerSignatureCanvas?.clear();
    handleFormData({
      ...formData,
      customerSignature: "",
    });
  };

  const onDrawEnd = (e: any) => {
    handleFormData({
      ...formData,
      customerSignatureCanvas: e,
      customerSignature: e?.getTrimmedCanvas()?.toDataURL("image/png"),
      customerSignatureTime: new Date().toISOString(),
    });
  };

  const handleNextButtonClick = () => {
    if (
      formData.customerSignature &&
      formData.customerSignTnC &&
      formData.customerSignatureCanvas
    ) {
      navigate("/digital-contract/contract/witness-and-agent-signatures");
      return;
    }
    return setError(true);
  };

  const captureAfterFrames = () => {
    frameCountRef.current++;
    if (frameCountRef.current >= 120) {
      handleImageCapture(videoRef.current);
      stopVideoStream(videoStream);
    } else requestAnimationFrame(captureAfterFrames);
  };

  const handleImageCapture = (videoEle: any) => {
    if (!videoEle) {
      setLoading(false);
      return;
    }
    let canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 300;
    let context = canvas.getContext("2d");
    if (!context) {
      setLoading(false);
      return;
    }
    context.drawImage(videoEle, 0, 0, 200, 300);
    const dataURI = canvas.toDataURL("image/png");
    getGeolocation()
      .then((coordinates) => {
        handleFormData({
          ...formData,
          customerPicture: dataURItoBlob(dataURI) ?? "",
          coordinates: coordinates as { latitude: string; longitude: string },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Header />
      <div className="w-100 h-100 " style={{ position: "relative" }}>
        {loading && (
          <Overlay>
            <PulseLoader
              color={variables.theme_blue}
              size={25}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </Overlay>
        )}
        {permissionError && (
          <Overlay
            style={{
              backgroundColor: variables.theme_dark_gray_transparent,
              alignItems: "flex-start",
            }}
          >
            <ResetPermissions />
          </Overlay>
        )}
        <>
          <video
            ref={videoRef}
            autoPlay
            muted
            width={0}
            height={0}
            onPlaying={() => requestAnimationFrame(captureAfterFrames)}
          />
          <Label
            required
            label="Signature of Customer"
            className="d-flex justify-between items-center"
            dynamicContent={
              <div>
                <Button
                  icon={ReloadIcon}
                  subType="icon"
                  type="button"
                  name=""
                  disabled={!formData?.customerSignature}
                  onClick={onClearButtonClick}
                />
              </div>
            }
          />
          <div className="signature-canvas-wrapper">
            <SignatureCanvas
              onDrawEnd={onDrawEnd}
              {...(error &&
                !formData.customerSignature && {
                  error: "This field is required.",
                })}
            />
          </div>
          <Checkbox
            title="I have agree to share my signature with Sun King for processing my loan application"
            defaultValue={formData.customerSignTnC}
            handleCheckboxChange={onCheckboxClick}
            {...(error && !formData.customerSignTnC
              ? {
                  error: "This field is required.",
                }
              : {})}
          />
        </>
      </div>
      <NavigationButtons handleNextButtonClick={handleNextButtonClick} />
    </>
  );
};

export default CustomerSignature;
